import React, { Suspense, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ApiContext } from '@api/api';
import useTranslationLgs from '@hooks/i18n/useTranslation.tsx';
import { InputCheckbox } from '@components/ringil3/Input/InputCheckbox/InputCheckbox.tsx';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import COHeadline from '@components/framework/headlines/COHeadline.tsx';
import FedexDisclaimer from '@components/RingilComponents/FedexDisclaimer.tsx';
import FedexLogo from '@components/RingilComponents/FedexLogo.tsx';
import { FEDEX_EULA_HOSTED_URL, FEDEX_EULA_TEXT, FEDEX_EULA_VERSION } from '@assets/fedex/fedexEula.ts';

// pdf.js is heavy and carries its own worker chunk - keep it out of the main bundle
const FedexEulaPdfPages = React.lazy(
  () => import('@app/pages/customer/CompanyProfile/FedexEulaPdfPages.tsx'),
);

interface ComponentProps {
  onAccepted: () => void;
}

/**
 * FedEx-mandated EULA gate. The Integrator guidance requires the solution to:
 *  - display the ENTIRE EULA on the registration screen (not behind a link),
 *  - require the user to SCROLL THROUGH it before acceptance is permitted,
 *  - require an acknowledgement checkbox,
 *  - require a button reading exactly "I accept the terms of FedEx EULA to start shipping."
 *
 * The agreement is therefore rendered page by page from the original FedEx PDF INSIDE our
 * own scroll container: an <iframe> would show the same document but its scroll position is
 * unreadable to us, which would silently break the mandated scroll-through requirement.
 * If the PDF fails to render we fall back to the bundled plain text, so the agreement is
 * always displayed in full.
 *
 * The checkbox statement and the button label are prescribed English strings - never localized.
 */
const FedexEulaGate: React.FC<ComponentProps> = ({ onAccepted }) => {
  const { tr } = useTranslationLgs();
  const { acceptFedexEula } = useContext(ApiContext);

  const scrollRef = useRef<HTMLDivElement>(null);
  const [scrolledToEnd, setScrolledToEnd] = useState(false);
  const [confirmedRead, setConfirmedRead] = useState(false);
  const [progress, setProgress] = useState(0);
  const [numPages, setNumPages] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pdfFailed, setPdfFailed] = useState(false);
  // every PDF page has painted (or the text fallback is shown) - before that the scroll
  // container is empty/short and would wrongly count as "read to the end"
  const [documentReady, setDocumentReady] = useState(false);
  const [pageWidth, setPageWidth] = useState(880);

  // pages are rendered at the container's inner width so the document always fills the panel
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const measure = () => setPageWidth(Math.max(320, el.clientWidth - 48));
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const max = el.scrollHeight - el.clientHeight;
    const ratio = max <= 0 ? 1 : Math.min(1, el.scrollTop / max);
    setProgress(ratio);
    if (numPages > 0) {
      setCurrentPage(Math.min(numPages, Math.floor(ratio * (numPages - 1)) + 1));
    }
    // a few px of tolerance - sub-pixel zoom levels never hit the exact bottom
    if (documentReady && el.scrollTop + el.clientHeight >= el.scrollHeight - 8) setScrolledToEnd(true);
  };

  // the text fallback has no async rendering - it is ready as soon as it is shown
  useEffect(() => {
    if (pdfFailed) setDocumentReady(true);
  }, [pdfFailed]);

  // once the whole document is on screen, content shorter than the viewport counts as read
  useEffect(() => {
    if (documentReady) onScroll();
  }, [documentReady]);

  const handleAccept = () => {
    acceptFedexEula({ eulaVersion: FEDEX_EULA_VERSION }, () => onAccepted());
  };

  const readingDone = scrolledToEnd;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-row items-center gap-4">
        <FedexLogo height={44} />
        <COHeadline variant={'h3'} title={tr('CompanyProfileFedex.eula.title', 'FedEx End User License Agreement')} />
      </div>
      <div className="text-sm text-content-secondary max-w-[760px]">
        {tr(
          'CompanyProfileFedex.eula.intro',
          'Before connecting a FedEx account, FedEx requires every user to read and accept their End User License Agreement. Scroll through the entire agreement to continue.',
        )}
      </div>

      {/* document viewer ------------------------------------------------ */}
      <div className="border border-content-quaternary rounded-lg overflow-hidden bg-white shadow-sm">
        {/* header: identity of the document + where you are in it */}
        <div className="flex items-center justify-between gap-4 px-4 py-3 border-b border-content-quaternary bg-neutral-50">
          <div className="flex flex-col">
            <span className="text-sm font-semibold text-content-primary">
              FedEx End User License Agreement (3rd Party Hosted)
            </span>
            <span className="text-xs text-content-secondary">FedEx Form No. 2002382 v 4 June 2024 Rev</span>
          </div>
          <div className="flex items-center gap-4 shrink-0">
            {!pdfFailed && numPages > 0 && (
              <span className="text-xs text-content-secondary tabular-nums">
                {tr('CompanyProfileFedex.eula.pageOf', 'Page {{current}} of {{total}}', {
                  current: currentPage,
                  total: numPages,
                })}
              </span>
            )}
            <a
              href={FEDEX_EULA_HOSTED_URL}
              target="_blank"
              rel="noreferrer"
              className="text-xs text-primary underline whitespace-nowrap">
              {tr('CompanyProfileFedex.eula.openPdf', 'Open PDF')}
            </a>
          </div>
        </div>

        {/* reading progress - makes the scroll-through requirement visible */}
        <div className="h-1 bg-neutral-200">
          <div
            className={`h-full transition-[width] duration-150 ${readingDone ? 'bg-green-500' : 'bg-primary'}`}
            style={{ width: `${Math.round(progress * 100)}%` }}
          />
        </div>

        {/* the agreement itself, scrolled by US so the scroll-through gate can be enforced */}
        <div
          ref={scrollRef}
          onScroll={onScroll}
          className="h-[70vh] min-h-[420px] overflow-y-auto bg-neutral-100 px-6 py-6"
          tabIndex={0}>
          {!pdfFailed ? (
            <Suspense
              fallback={
                <div className="text-sm text-content-secondary py-10 text-center">
                  {tr('CompanyProfileFedex.eula.loading', 'Loading the agreement…')}
                </div>
              }>
              <FedexEulaPdfPages
                width={pageWidth}
                onLoaded={setNumPages}
                onAllRendered={() => setDocumentReady(true)}
                onFailed={() => setPdfFailed(true)}
                loadingLabel={tr('CompanyProfileFedex.eula.loading', 'Loading the agreement…')}
              />
            </Suspense>
          ) : (
            // fallback: the same agreement as bundled text, so it is never hidden
            <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed m-0 bg-white p-6 rounded-md">
              {FEDEX_EULA_TEXT}
            </pre>
          )}
        </div>

        {/* footer: acknowledgement + the two prescribed strings - shown only once the reader
            has reached the end of the agreement, before that just the scroll hint */}
        <div className="flex flex-col gap-3 px-4 py-4 border-t border-content-quaternary bg-white">
          {!readingDone ? (
            <div className="text-xs text-content-secondary">
              {tr(
                'CompanyProfileFedex.eula.scrollHint',
                'Scroll to the end of the agreement to enable acceptance.',
              )}
            </div>
          ) : (
            <>
              <InputCheckbox
                label={'I confirm that I have read the FedEx End User License Agreement.'}
                value={confirmedRead}
                onChange={(_, value) => setConfirmedRead(!!value)}
              />
              <div>
                <RingilButton
                  label={'I accept the terms of FedEx EULA to start shipping.'}
                  onClick={handleAccept}
                  disabled={!confirmedRead}
                />
              </div>
            </>
          )}
        </div>
      </div>

      <FedexDisclaimer />
    </div>
  );
};

export default FedexEulaGate;
